import { isAfter } from 'date-fns';
import { capitalize, getCompanyTypeByName } from '@app/helpers';
import { CompanyTypeNames, CompanyTypes } from '@app/models';

export interface DN {
  firstName: string;
  lastName: string;
  middleName: string;
  email: string;
  iin: string;
  company?: {
    bin: string;
    name: string;
    type: CompanyTypes;
  };
}

export interface NcaKeyInfoResponse {
  certNotAfter?: string;
  certNotBefore?: string;
  subjectDn?: string;
}

function getDnValue(subjectDn: string[], key: string): string | null {
  const data = subjectDn.find((item) => item.split('=')[0] === key);
  if (!data) {
    return null;
  }
  return data.split('=')[1] || null;
}

export function parseSubjectDnParts(subjectDnParts: string[]): DN | null {
  try {
    const cnRaw = getDnValue(subjectDnParts, 'CN');
    const serialRaw = getDnValue(subjectDnParts, 'SERIALNUMBER');
    if (!cnRaw || !serialRaw) {
      return null;
    }

    const cn = cnRaw.split(' ');
    const dnData = {} as DN;
    dnData.lastName = cn[0] ? capitalize(cn[0]).trim() : '';
    dnData.firstName = cn[1] ? capitalize(cn[1]).trim() : '';
    const g = getDnValue(subjectDnParts, 'G');
    dnData.middleName = g ? capitalize(g).trim() : '';
    const emailVal = getDnValue(subjectDnParts, 'E');
    dnData.email = emailVal ? emailVal.toLowerCase().trim() : '';
    dnData.iin = serialRaw.replace(/^IIN/i, '').trim();

    const ou = getDnValue(subjectDnParts, 'OU');
    const o = getDnValue(subjectDnParts, 'O');
    if (ou && o) {
      const bin = ou.replace(/^BIN/i, '').trim();
      let name = '';
      let companyType = '' as unknown as CompanyTypes;
      Object.values(CompanyTypeNames).forEach((typeName) => {
        if (o.toUpperCase().indexOf(typeName) > -1) {
          name = o.substring(typeName.length, o.length);
          companyType = getCompanyTypeByName(typeName);
        }
      });
      dnData.company = {
        bin,
        name: name.replace(/\\/g, '').replace(/["]+/g, '').trim(),
        type: companyType,
      };
    }

    return dnData;
  } catch {
    return null;
  }
}

export function parseNcaKeyInfoResponse(responseObject: NcaKeyInfoResponse): {
  dn: DN | null;
  expired: boolean;
} {
  const { certNotAfter, subjectDn } = responseObject;

  if (certNotAfter && isAfter(new Date(), new Date(certNotAfter))) {
    return { dn: null, expired: true };
  }

  if (!subjectDn) {
    return { dn: null, expired: false };
  }

  const dn = parseSubjectDnParts(subjectDn.split(','));

  return { dn, expired: false };
}
